import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { TabStyles } from '../../styles/TabStyles';
import { makeTestId } from '../../utils/Declarative';
import { ElemAttrs } from '../../utils/HtmlAttrs';
import { EventUtilsOptOut, mergeEventHandlers } from '../../utils/ReactHelpers';
import { ColorGridPreset, ColorPickerPresets, isColorInPreset } from './ColorPickerPresets';

export interface ColorSwatchProps {
  color: string;
  disabled?: boolean;
  onClick: (color: string) => void;
  selected: boolean;
  size: number;
  testId: string;
}

const ColorSwatchSize: number = 15;
const ColorSwatchDivider = () => <div style={{ width: ColorSwatchSize, height: 4 }} />;

const ColorSwatchStyleName = TypeStyle.style({
  boxSizing: 'border-box',
  height: ColorSwatchSize,
  width: ColorSwatchSize,
  $nest: {
    '&:hover, &:active, &[aria-selected="true"]': { position: 'relative' },
    '&:focus-visible': { position: 'relative', outlineOffset: -1, outline: `1px solid ${TabStyles.Colors.Focus}`, boxShadow: `0 0 4px 0 ${TabStyles.Colors.Focus}` },
    '&[aria-selected="true"]::after': { outline: `1px solid ${TabStyles.Colors.L8}` },
    '&:hover::after, &:active::after': { outline: `1px solid ${TabStyles.Colors.L9}` },
    '&:hover::after, &:active::after, &[aria-selected="true"]::after': {
      border: `1px solid white`,
      bottom: -1,
      content: `""`,
      left: -1,
      pointerEvents: 'none',
      position: 'absolute',
      right: -1,
      top: -1,
    },
  }
});

const DataColorAttr = 'data-color';
export const ColorSwatchWidget = React.memo(React.forwardRef<HTMLDivElement, ColorSwatchProps & Omit<ElemAttrs, keyof ColorSwatchProps>>((props, ref) => {
  const { color: propsColor, className, disabled, onClick, selected, size, style, testId, ...htmlAttrs } = props;
  const color = propsColor.toLowerCase();
  const handleClick = React.useCallback(() => props.onClick(color), [props.onClick, color]);
  return (
    <div
      {...EventUtilsOptOut}
      aria-disabled={disabled}
      aria-label={color} // not sure how useful this actually is, but otherwise screan reader reads 'blank' for every swatch, which is even worse
      aria-selected={selected}
      data-tb-test-id={makeTestId(testId, 'ColorSwatch', color)}
      {...htmlAttrs}
      className={TypeStyle.classes(className, ColorSwatchStyleName, disabled && 'disabled')}
      {...{ [DataColorAttr]: color }}
      onClick={disabled ? undefined : handleClick}
      ref={ref}
      style={{
        backgroundColor: color,
        height: size,
        width: size,
        ...style,
      }}
    />
  );
}));
ColorSwatchWidget.displayName = 'ColorSwatchWidget';

export { ColorGridPreset };

export interface ColorGridWidgetProps extends Pick<React.AriaAttributes, 'aria-label' | 'aria-labelledby'> {
  /**
   * The set of columns that will be displayed in the color grid.
   * @default ColorGridPreset.Dark
   */
  colorsPreset?: ColorGridPreset;

  disabled?: boolean;

  /**
   * Callback function to notify user when a color swatch is picked
   */
  handleSwatchClicked: (color: string) => void;

  /**
   * Color that is currently selected
   */
  selectedColor?: string;

  /**
   * Test id to be attached to the dialog and its contents.
   * The suffix '-ColorGridWidget' will be automatically added
   */
  testId: string;
}

const ColorGridStyleName = TypeStyle.style({ display: 'flex', gap: 3 });
export class ColorGridWidget extends React.PureComponent<ColorGridWidgetProps & Omit<ElemAttrs, keyof ColorGridWidgetProps>> {
  public static defaultProps: Pick<ColorGridWidgetProps, 'colorsPreset'> = { colorsPreset: ColorGridPreset.Dark };

  public render(): JSX.Element {
    const { className, colorsPreset, disabled, handleSwatchClicked, selectedColor, testId, ...htmlAttrs } = this.props;

    const { rowCount, colCount, gridItems } = this.buildColorSwatchGrid();
    return <div
      data-tb-test-id={makeTestId(this.props.testId, 'ColorGrid')}
      {...htmlAttrs}
      aria-colcount={colCount}
      aria-rowcount={rowCount}
      className={TypeStyle.classes(className, ColorGridStyleName)}
      children={gridItems}
      onKeyDown={this.handleKeyDown}
      role='grid'
    />;
  }

  private handleKeyDown: React.KeyboardEventHandler<HTMLElement> = mergeEventHandlers(e => {
    const cellElem = e.target as HTMLElement;
    const gridElem = e.currentTarget as HTMLElement;
    const color = cellElem.getAttribute(DataColorAttr);
    if (!color) { return; }

    const rowIndex = Number(cellElem.getAttribute('aria-rowindex')) - 1;
    const colIndex = Number(cellElem.getAttribute('aria-colindex')) - 1;
    const focusCell = (row: number, col: number) =>
      e.currentTarget.querySelector<HTMLElement>(`[aria-rowindex="${row + 1}"][aria-colindex="${col + 1}"]`)?.focus();

    const colCount = Number(gridElem.getAttribute('aria-colcount'));
    const rowCount = Number(gridElem.getAttribute('aria-rowcount'));

    // we don't do any bounds checking on the indexes in here because the focus query will simply fail if its out of bounds
    const handlers: { [key: string]: () => void } = {
      'Enter': () => this.props.handleSwatchClicked(color),
      ' ': () => handlers.Enter(),
      'ArrowLeft': () => focusCell(rowIndex, colIndex - 1),
      'ArrowRight': () => focusCell(rowIndex, colIndex + 1),
      'ArrowDown': () => focusCell(rowIndex + 1, colIndex),
      'ArrowUp': () => focusCell(rowIndex - 1, colIndex),
      'Ctrl+Home': () => focusCell(0, 0),
      'Home': () => focusCell(rowIndex, 0),
      'End': () => focusCell(rowIndex, colCount - 1),
      'Ctrl+End': () => focusCell(rowCount - 1, colCount - 1),
    };

    const key = `${e.ctrlKey ? 'Ctrl+' : ''}${e.key}`;
    if (handlers[key]) {
      handlers[key]();
      e.preventDefault();
      e.stopPropagation();
    }
  }, e => this.props.onKeyDown?.(e));

  private buildColorSwatchGrid(): { rowCount: number, colCount: number, gridItems: React.ReactNode } {
    const { colorsPreset, selectedColor } = this.props;

    const selectedColorIsInPreset = !!selectedColor && isColorInPreset(selectedColor, colorsPreset!);
    const colors = ColorPickerPresets[colorsPreset!];
    const colCount = colors.length;
    const rowCount = colors[0].filter(c => c !== '|').length; // aria-rowcount and aria-rowindex should not include dividers

    const columns: React.ReactNodeArray = [];
    for (let colIdx = 0; colIdx < colCount; colIdx++) {
      const currentColumn: React.ReactNodeArray = [];

      let seenDivider = false;
      let currentGroup: React.ReactNodeArray = [];
      const closeGroup = () => {
        currentColumn.push(<div children={currentGroup} style={{ border: `1px solid ${TabStyles.Colors.L6}` }} key={`group-${seenDivider ? 1 : 0}`} />);
        currentGroup = [];
      };

      for (let rowIdx = 0; rowIdx < colors[colIdx].length; rowIdx++) {
        const color: string = colors[colIdx][rowIdx].toLowerCase();

        if (color === '|') {
          closeGroup();
          currentColumn.push(<ColorSwatchDivider key='divider' />);
          seenDivider = true;
          continue;
        }

        const selected = selectedColor === color;
        currentGroup.push(<ColorSwatchWidget
          aria-colindex={colIdx + 1}
          aria-rowindex={rowIdx + (seenDivider ? 0 : 1)}
          color={color}
          disabled={this.props.disabled}
          key={'swatch-' + color}
          onClick={this.props.handleSwatchClicked}
          role='gridcell'
          selected={selected}
          size={ColorSwatchSize}
          testId={this.props.testId}
          tabIndex={selected || (colIdx === 0 && rowIdx === 0 && !selectedColorIsInPreset) ? 0 : -1}
        />);
      }
      closeGroup();

      columns.push(<div children={currentColumn} key={'column-' + colIdx} />);
    }

    return { rowCount, colCount, gridItems: columns };
  }
}
